import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import {
  selectActivitys,
  selectActiveOperatorsAndVehicles,
} from '../../../../gql/selectors/actions';
import { buildRegisteredByOperators } from '../../../../utils/registeredByOperators';

// Data static
import { groupLanes, groupTracks } from '../../../../data/orders';

// Constants
import { formActionNaming } from '../../../../components/forms/ActionForms/constants';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../utils/draftScopes';

/**
 * Catálogos que alimentan el form de fase 1 Y las tabs del wizard (Location/
 * Edition leen las listas desde NEW_ACTION_DATA — el form las registra como
 * valores). Exportado para el auto-create P9 (`createBasicAction`), que
 * salta el form pero debe sembrar el MISMO contrato en sessionStorage.
 */
export const fetchActionFormCatalogs = async () => {
  const [
    { data },
    {
      data: { capitols },
    },
    {
      data: { classifications },
    },
    {
      data: { predefinedOrders },
    },
    activitys,
  ] = await Promise.all([
    client.query({ query: GET_SELECTORS_DATA }),
    client.query({ query: FETCH_CAPITOLS }),
    client.query({ query: FETCH_CLASSIFICATIONS }),
    client.query({ query: FETCH_PREDEFINED_ORDERS }),
    selectActivitys(),
  ]);
  return {
    ...data,
    capitols,
    classifications,
    groupTracks,
    groupLanes,
    predefinedOrders,
    activitys,
  };
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      return await fetchActionFormCatalogs();
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Action loading error:', err);
    return true;
  }
};

export const NewActionLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async () => {
  // Las copias del asistente, la lista de fotos y el croquis son del borrador
  // en curso (`draftId`, lo deja `pass-data`): si eran de otro, fuera.
  // Síncrono y antes de cualquier `await` (spec 2026-11-02
  // wizard-draft-ownership). El padre no se mira aquí: el paso 1 solo se
  // pinta tras el POST de su puerta, que escribe el padre de la URL.
  const draftId = readCreationDraftId();
  claimDraftScope(ACTION_CREATION_SCOPE, draftId);
  claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const stringifyForm = sessionStorage.getItem(
    formActionNaming.NEW_ACTION_DATA
  );

  const loaderToResolve = await loadFormOptions(userData);
  const nameOperator = `${userData?.user?.user?.name} ${
    userData?.user?.user.surName.split(' ')[0]
  }`;

  // Operarios para el dropdown "Operario que registra" (fase básica).
  let registeredByOperators;
  try {
    const { operators } = await selectActiveOperatorsAndVehicles(true);
    registeredByOperators = buildRegisteredByOperators(operators, userData);
  } catch {
    registeredByOperators = buildRegisteredByOperators([], userData);
  }

  if (stringifyForm) {
    const sessionStorageData = JSON.parse(
      decompressSessionStorage(stringifyForm)
    );
    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      ...sessionStorageData,
      operator: nameOperator,
      operators: registeredByOperators,
    };

    return combinedData;
  }

  const combinedData = {
    // team field removed - users no longer have teamIDs
    ...loaderToResolve,
    operator: nameOperator,
    operators: registeredByOperators,
  };

  return combinedData;
};

  return { data: load() };
};
